import { useRef, useState } from "react";
import { saveBinaryWithPicker } from "../flashtools/lib/saveAs";
import { Button } from "../components/ui/button";
import HexViewer from "../components/HexViewer";
import { MCU_PROFILES, getProfile, detectProfile, generateRecovery } from "../lib/recoveryGen";
import { RenesasFlashTool } from "../flashtools/components/RenesasFlashTool";
import { LifeBuoy, Upload, Download, Terminal, FileDigit, CircuitBoard, X } from "lucide-react";

export default function RecoveryGenerator() {
  return <RecoveryGeneratorInner />;
}

function RecoveryGeneratorInner() {
  const [profileId, setProfileId] = useState(MCU_PROFILES[0].id);
  const [original, setOriginal] = useState(null);
  const [fileName, setFileName] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [hexText, setHexText] = useState("");
  const [mode, setMode] = useState("remap");
  const [logs, setLogs] = useState([]);
  const [view, setView] = useState("recovery");
  const [showRenesas, setShowRenesas] = useState(false);
  const fileRef = useRef(null);

  const profile = getProfile(profileId);

  const onFile = async (f) => {
    if (!f) return;
    const buf = new Uint8Array(await f.arrayBuffer());
    setOriginal(buf);
    setFileName(f.name);
    setRecovery(null);
    setHexText("");
    setView("original");
    const det = detectProfile(buf);
    if (det) setProfileId(det.id);
    setLogs([
      { level: "info", msg: `Selected ${f.name} — ${buf.length} bytes (0x${buf.length.toString(16).toUpperCase()})` },
      det
        ? { level: "ok", msg: `Auto-detected MCU: ${det.id} — ${det.label}` }
        : { level: "warn", msg: `Could not auto-detect MCU — pick a profile manually before generating.` },
    ]);
  };

  const run = () => {
    if (!original) return;
    const res = generateRecovery(original, profile);
    setLogs(res.logs);
    if (!res.ok) { setRecovery(null); setHexText(""); return; }
    setMode(res.mode);
    if (res.mode === "hex") { setHexText(res.hexText); setRecovery(null); setView("original"); }
    else { setRecovery(res.bytes); setHexText(""); setView("recovery"); }
  };

  const download = async () => {
    const base = fileName.replace(/\.bin$/i, "") || profile.id;
    if (mode === "hex" && hexText) {
      await saveBinaryWithPicker(new TextEncoder().encode(hexText), `${base}.hex`, {});
      return;
    }
    if (!recovery) return;
    await saveBinaryWithPicker(recovery, `RECOVERY ${base}.bin`, {});
  };

  const isHex = profile.mode === "hex";
  const shown = view === "recovery" && recovery ? recovery : original;
  const highlight = view === "recovery" && recovery ? (profile.patches || []).map((p) => profile.dataOffset + p.offset) : [];
  const canDownload = mode === "hex" ? !!hexText : !!recovery;

  const LEVELS = { info: "text-slate-400", ok: "text-emerald-400", error: "text-cyan-300", warn: "text-yellow-400" };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><LifeBuoy className="h-4 w-4" /> Recovery</div>
          <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Recovery Generator</h1>
          <p className="text-slate-500 text-sm mt-1">Auto-detects the MCU from the .bin: R8C → memory-mapped recovery, RH850 / V850 → Intel HEX.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold">MCU Profile (auto-detected)</label>
              <select data-testid="recovery-profile-select" value={profileId} onChange={(e) => setProfileId(e.target.value)}
                className="w-full h-10 rounded-md bg-[#121824] border border-slate-700 text-slate-100 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400">
                {MCU_PROFILES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <p className="text-[11px] font-mono-tt text-slate-500 pt-1">
                {isHex
                  ? "Mode: BIN → Intel HEX (no remap)"
                  : `In 0x${profile.inputSize.toString(16).toUpperCase()} → Out 0x${profile.outputSize.toString(16).toUpperCase()} · data @0x${profile.dataOffset.toString(16).toUpperCase()}`}
              </p>
            </div>

            <input ref={fileRef} type="file" accept=".bin,.BIN" className="hidden" data-testid="recovery-file-input"
              onChange={(e) => onFile(e.target.files?.[0])} />
            <Button data-testid="recovery-upload-btn" onClick={() => fileRef.current?.click()}
              variant="outline" className="w-full border-slate-700 text-slate-200 hover:bg-slate-800">
              <Upload className="h-4 w-4 mr-2" /> {fileName || "Select original .bin"}
            </Button>

            <Button data-testid="recovery-generate-btn" onClick={run} disabled={!original}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-slate-950 font-bold disabled:opacity-40">
              <FileDigit className="h-4 w-4 mr-2" /> {isHex ? "Convert to HEX" : "Generate Recovery"}
            </Button>

            <Button data-testid="recovery-download-btn" onClick={download} disabled={!canDownload}
              variant="outline" className="w-full border-emerald-900/60 text-emerald-400 hover:bg-emerald-950/40 disabled:opacity-40">
              <Download className="h-4 w-4 mr-2" /> Download {mode === "hex" ? ".hex" : "Recovery .bin"}
            </Button>
          </div>

          <div className="rounded-xl border border-slate-800 bg-[#080b11] overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800 bg-[#0c1017]">
              <Terminal className="h-4 w-4 text-cyan-400" />
              <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Process Console</span>
            </div>
            <div data-testid="recovery-console" className="p-4 font-mono-tt text-xs space-y-1 min-h-[160px] max-h-[320px] overflow-y-auto">
              {logs.length === 0 && <div className="text-slate-600">Awaiting input…</div>}
              {logs.map((l, i) => (
                <div key={i} className={LEVELS[l.level] || "text-slate-400"}>
                  <span className="text-slate-600 mr-2">{String(i + 1).padStart(2, "0")}</span>{l.msg}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold mr-1">BIN Viewer</span>
            <button data-testid="recovery-view-original" onClick={() => setView("original")} disabled={!original}
              className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-wider border transition-colors disabled:opacity-40 ${view === "original" ? "bg-slate-200 text-slate-950 border-slate-200" : "bg-[#121824] text-slate-400 border-slate-700 hover:border-slate-500"}`}>Original</button>
            {!isHex && (
              <button data-testid="recovery-view-recovery" onClick={() => setView("recovery")} disabled={!recovery}
                className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-wider border transition-colors disabled:opacity-40 ${view === "recovery" ? "bg-red-500 text-white border-red-500" : "bg-[#121824] text-slate-400 border-slate-700 hover:border-slate-500"}`}>Recovery</button>
            )}
            {shown && <span className="ml-auto text-[11px] font-mono-tt text-slate-500">{shown.length} bytes · 0x{shown.length.toString(16).toUpperCase()}</span>}
          </div>
          {shown ? (
            <HexViewer data={shown} testid="recovery-hexviewer" highlight={highlight} />
          ) : (
            <div className="rounded-lg border border-slate-800 bg-[#080b11] p-12 text-center text-slate-600 font-mono-tt text-sm">
              Select an original .bin to inspect it here.
            </div>
          )}

          {mode === "hex" && hexText && (
            <div className="rounded-lg border border-slate-800 bg-[#080b11] overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-800 bg-[#0c1017] text-xs uppercase tracking-widest text-cyan-300 font-semibold">Intel HEX Output</div>
              <pre data-testid="recovery-hex-output" className="overflow-auto max-h-[260px] font-mono-tt text-[11px] text-slate-300 p-3">{hexText.split("\r\n").slice(0, 1500).join("\n")}{hexText.split("\r\n").length > 1500 ? "\n… (preview truncated — full file in download)" : ""}</pre>
            </div>
          )}
        </div>
      </div>

      {showRenesas && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-2" data-testid="renesas-modal">
          <div className="relative border border-cyan-500/30 bg-[#07090E] shadow-2xl overflow-hidden flex flex-col w-[95vw] max-w-[1200px] h-[92vh] rounded-xl">
            <div className="flex items-center justify-between px-4 py-2 border-b border-cyan-900/40 bg-[#0B0F19] shrink-0">
              <span className="font-head font-bold text-sm text-cyan-400 uppercase tracking-wide flex items-center gap-2">
                <CircuitBoard className="h-4 w-4" /> Renesas Flash Tool
              </span>
              <button data-testid="renesas-modal-close" onClick={() => setShowRenesas(false)} title="Tutup"
                className="h-7 w-7 inline-flex items-center justify-center rounded text-slate-400 hover:text-white hover:bg-slate-800">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-3">
              <RenesasFlashTool />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
